import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import ScriptLoader from '../page/components/script-loader';

import { withError } from '../shared/components/error-boundary';
import { useThirdPartyTags } from '../hooks/useSiteConfig';
import usePagComponents from '../hooks/usePageComponents';
import AboveTheFoldOnlyServerRender from '../shared/components/above-the-fold-only-server-render';
import { Helpers } from '../core/src/helpers';

/**
 * Helper function to get the resource hints for the scripts to use on the server
 * @param {*} scripts
 * @param {*} vanityUrl
 * @returns
 */
export const getResourceHint = (scripts, slugInfo) => {
    try {
        const { vanityUrl, nextQuery } = slugInfo;
        const { hash } = nextQuery || {};
        //also include next graphql query in resource hints
        const initialArray = hash
            ? [
                  {
                      href: `/us-en/shop/app/api/web/graphql/page/${encodeURIComponent(vanityUrl)}/${hash}`,
                      rel: 'prefetch',
                  },
              ]
            : [];

        return mapScriptSettings(scripts, vanityUrl).reduce((r, script) => {
            if (script.resourceHint) {
                return r.concat({ href: script.src, rel: script.resourceHint, as: script.as || 'script' });
            }
            return r;
        }, initialArray);
    } catch (e) {
        return [];
    }
};

export const mapScriptSettings = (scripts, vanityUrl) => {
    return (scripts || []).reduce((r, script) => {
        const { settings = [], src, customLoader, noScript } = script;
        const currentSettings = settings.find(({ path }) => {
            return !path || new RegExp(path).test(vanityUrl);
        });
        if (!currentSettings) return r;
        const inlineLoader = currentSettings.customLoader || customLoader;
        const noScriptContent = currentSettings.noScript || noScript;
        if (inlineLoader) {
            return r.concat({ ...currentSettings, customLoader: inlineLoader, noScript: noScriptContent });
        }
        if (src?.length < 1) return r;
        const scriptSettings = src.map(url => ({ src: url, ...currentSettings, noScript: noScriptContent }));
        if (scriptSettings.length > 0) {
            return r.concat(scriptSettings);
        }
        return r;
    }, []);
};

const useScriptLoader = scriptProp => {
    const { vanityUrl } = usePagComponents([]);
    const { advancedScriptLoader: scripts } = useThirdPartyTags(['advancedScriptLoader']);
    //if script prop is provided, use that
    if (scriptProp && scriptProp.length > 0) {
        return scriptProp;
    }

    //loop through the scripts and get the script settings for each type
    return mapScriptSettings(scripts, vanityUrl);
};

/**
 * track the loaded scripts to avoid loading them again
 */
const scriptIsLoaded = {};
const noScriptSanitizationOptions = {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['src', 'height', 'width', 'style'],
    FORCE_BODY: true,
};
const gtmNoScriptIframeSrc = /^https:\/\/www\.googletagmanager\.com\/ns\.html\?id=GTM-[A-Z0-9-]+$/i;

const sanitizeNoScript = noScript => {
    const noScriptContent = noScript.trim().replace(/^("|')([\s\S]*)\1$/, '$2');
    const noScriptWithValidatedIframeSrc = noScriptContent.replace(/<iframe\b[^>]*>/gi, iframeTag => {
        const srcMatch = iframeTag.match(/\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
        const iframeSrc = srcMatch?.[1] || srcMatch?.[2] || srcMatch?.[3];

        return gtmNoScriptIframeSrc.test(iframeSrc)
            ? iframeTag
            : iframeTag.replace(/\s+src\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i, '');
    });

    return Helpers.purifyHTML(noScriptWithValidatedIframeSrc, noScriptSanitizationOptions);
};

export const ScriptTag = React.memo(({ src, priority, renderOn, execute, once = true, customLoader, noScript }) => {
    const [isMounted, setIsMounted] = useState(false);
    const customLoaderContent = customLoader?.replace(/^\s*<script\b[^>]*>/i, '').replace(/<\/script>\s*$/i, '');
    const scriptTag = customLoader ? (
        <script type="text/javascript">{Helpers.purifyHTML(customLoaderContent)}</script>
    ) : execute === 'async' ? (
        <script src={src} async />
    ) : execute === 'defer' ? (
        <script src={src} defer />
    ) : (
        <script src={src} />
    );
    useEffect(() => {
        setIsMounted(true);
    }, []);

    const noScriptContent = noScript ? sanitizeNoScript(noScript) : null;
    const noScriptTag = noScriptContent ? <noscript>{noScriptContent}</noscript> : null;
    if (!customLoader && (!src || (scriptIsLoaded[src] && once)))
        return noScriptTag ? <Helmet>{noScriptTag}</Helmet> : null;
    if (isMounted && !customLoader) {
        //only track the script is loaded on the client side
        scriptIsLoaded[src] = true;
    }
    if (priority === 'high') {
        return (
            <Helmet>
                {scriptTag}
                {noScriptTag}
            </Helmet>
        );
    } else {
        return (
            <>
                <AboveTheFoldOnlyServerRender skip={true} renderOn={renderOn}>
                    {customLoader ? <Helmet>{scriptTag}</Helmet> : <ScriptLoader url={src} once={once} />}
                </AboveTheFoldOnlyServerRender>
                {noScriptTag && <Helmet>{noScriptTag}</Helmet>}
            </>
        );
    }
});

export default withError(({ scripts: scriptProp, css }) => {
    const scripts = useScriptLoader(scriptProp);
    if (scripts?.length < 1) return null;
    return (
        <>
            {scripts.map((script, i) => (
                <div key={i}>
                    <ScriptTag {...script} />
                </div>
            ))}
            {css && css.length > 0 && (
                <Helmet>
                    {css.map((css, i) => (
                        <link key={i} rel="stylesheet" href={css} />
                    ))}
                </Helmet>
            )}
        </>
    );
});
